<script setup>
// Чат для /live у двох режимах:
//   panel — закріплена колонка праворуч від плеєра (ширина тягнеться мишею)
//   popup — плаваюче вікно поверх сторінки (drag / resize / прозорість / zoom)
//
// Усередині — один із двох чатів на вибір в адмінці (див. props.source):
// Twitch або власний.
//
// Два обмеження Twitch, які визначили верстку (ті самі, що й у
// TwitchChatWidget — інакше власник і модератори не можуть писати в чат):
//   1. нічого не можна класти ПОВЕРХ iframe (захист від clickjacking),
//      тому панель керування завжди окремою смугою над фреймом;
//   2. на самому iframe (і його предках) не має бути CSS transform —
//      навіть тотожного scale(1), тому transform ставимо лише коли
//      масштаб реально змінили.

// Канал за замовчуванням. Інший приходить пропом — ефір буває і з чужого
// каналу, тоді поруч із плеєром має бути його чат, а не наш порожній
const DEFAULT_CHANNEL = 'olsior'

const open = defineModel('open', { type: Boolean, default: true })
const mode = defineModel('mode', { type: String, default: 'panel' }) // panel | popup
const width = defineModel('width', { type: Number, default: 26 }) // % ширини екрана

// Примусовий ряд: CSS-фулскрін на iPhone ставить плеєр і чат поруч навіть
// на вузькому екрані. Тоді мобільні правила ширини вимикаємо — інакше
// max-md:!w-full перебиває задану ширину, і чат займає пів екрана
const props = defineProps({
  forceRow: { type: Boolean, default: false },
  // Який чат показуємо — обирається в адмінці (Strapi → Прямий ефір):
  //   'twitch' — фрейм чату Twitch,
  //   'live'   — наш власний чат на вебсокетах: миттєві повідомлення,
  //              до пʼяти картинок під спойлером, смайли 7TV і модерація
  //              (див. backend/src/chat)
  // Чат сайту (comments.app) і обговорення Telegram прибрані
  source: { type: String, default: 'twitch' },
  // Кімната власного чату
  chatPageId: { type: String, default: 'live' },
  // Чий чат Twitch показуємо
  chatTwitch: { type: String, default: DEFAULT_CHANNEL },
  chatTitle: { type: String, default: 'Чат ефіру' }
})

const channel = computed(() => props.chatTwitch || DEFAULT_CHANNEL)

const isLiveChat = computed(() => props.source === 'live')
// Усе, чого ми не впізнали, лишається чатом Twitch — це давня поведінка
// сторінки, і ламати її незнайомим значенням з адмінки не варто
const isTwitchChat = computed(() => !isLiveChat.value)

const MIN_PANEL = 15
const MAX_PANEL = 50

const isPopup = computed(() => mode.value === 'popup')

// --- Плаваюче вікно ---
const pos = ref({ x: 0, y: 0 })
const size = ref({ width: 380, height: 560 })
const opacity = ref(1)
const zoom = ref(1)

const MIN_WIDTH = 220
const MIN_HEIGHT = 240
const MIN_ZOOM = 0.5
const MAX_ZOOM = 2
const ZOOM_STEP = 0.1

const STORAGE_KEY = 'live_chat_settings'

const persist = () => {
  if (!import.meta.client) return
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        open: open.value,
        mode: mode.value,
        width: width.value,
        size: size.value,
        opacity: opacity.value,
        zoom: zoom.value
      })
    )
  } catch (e) {}
}

const restore = () => {
  if (!import.meta.client) return
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
    if (!saved) return
    if (typeof saved.open === 'boolean') open.value = saved.open
    if (saved.mode === 'panel' || saved.mode === 'popup') mode.value = saved.mode
    if (typeof saved.width === 'number') {
      width.value = Math.min(MAX_PANEL, Math.max(MIN_PANEL, saved.width))
    }
    if (saved.size?.width && saved.size?.height) size.value = saved.size
    if (typeof saved.opacity === 'number') opacity.value = saved.opacity
    if (typeof saved.zoom === 'number') zoom.value = saved.zoom
  } catch (e) {}
}

watch([open, mode, width, size, opacity, zoom], persist, { deep: true })

const clampToViewport = () => {
  if (!import.meta.client) return
  const maxX = window.innerWidth - size.value.width
  const maxY = window.innerHeight - size.value.height
  pos.value.x = Math.min(Math.max(pos.value.x, 0), Math.max(maxX, 0))
  pos.value.y = Math.min(Math.max(pos.value.y, 0), Math.max(maxY, 0))
}

// Перший показ поповера ставимо в правий нижній кут
watch(
  [open, isPopup],
  ([isOpen, popup]) => {
    if (!isOpen || !popup || !import.meta.client) return
    if (pos.value.x === 0 && pos.value.y === 0) {
      pos.value = {
        x: window.innerWidth - size.value.width - 24,
        y: window.innerHeight - size.value.height - 24
      }
    }
    clampToViewport()
  },
  { immediate: true }
)

// --- Перетягування вікна ---
const dragging = ref(false)
let dragStart = { x: 0, y: 0, posX: 0, posY: 0 }

const onDragStart = (e) => {
  dragging.value = true
  const point = e.touches ? e.touches[0] : e
  dragStart = {
    x: point.clientX,
    y: point.clientY,
    posX: pos.value.x,
    posY: pos.value.y
  }
  window.addEventListener('mousemove', onDragMove)
  window.addEventListener('mouseup', onDragEnd)
  window.addEventListener('touchmove', onDragMove, { passive: false })
  window.addEventListener('touchend', onDragEnd)
}

const onDragMove = (e) => {
  if (!dragging.value) return
  e.preventDefault()
  const point = e.touches ? e.touches[0] : e
  pos.value.x = dragStart.posX + (point.clientX - dragStart.x)
  pos.value.y = dragStart.posY + (point.clientY - dragStart.y)
  clampToViewport()
}

const onDragEnd = () => {
  dragging.value = false
  window.removeEventListener('mousemove', onDragMove)
  window.removeEventListener('mouseup', onDragEnd)
  window.removeEventListener('touchmove', onDragMove)
  window.removeEventListener('touchend', onDragEnd)
}

// --- Ресайз вікна ---
const resizing = ref(false)
let resizeStart = { x: 0, y: 0, width: 0, height: 0 }

const onResizeStart = (e) => {
  resizing.value = true
  const point = e.touches ? e.touches[0] : e
  resizeStart = {
    x: point.clientX,
    y: point.clientY,
    width: size.value.width,
    height: size.value.height
  }
  window.addEventListener('mousemove', onResizeMove)
  window.addEventListener('mouseup', onResizeEnd)
  window.addEventListener('touchmove', onResizeMove, { passive: false })
  window.addEventListener('touchend', onResizeEnd)
}

const onResizeMove = (e) => {
  if (!resizing.value) return
  e.preventDefault()
  const point = e.touches ? e.touches[0] : e
  size.value.width = Math.max(MIN_WIDTH, resizeStart.width + (point.clientX - resizeStart.x))
  size.value.height = Math.max(MIN_HEIGHT, resizeStart.height + (point.clientY - resizeStart.y))
  clampToViewport()
}

const onResizeEnd = () => {
  resizing.value = false
  window.removeEventListener('mousemove', onResizeMove)
  window.removeEventListener('mouseup', onResizeEnd)
  window.removeEventListener('touchmove', onResizeMove)
  window.removeEventListener('touchend', onResizeEnd)
}

// --- Ресайз панелі (тягнемо ліву межу колонки) ---
const panelResizing = ref(false)

const onPanelResizeStart = () => {
  panelResizing.value = true
  window.addEventListener('mousemove', onPanelResizeMove)
  window.addEventListener('mouseup', onPanelResizeEnd)
  window.addEventListener('touchmove', onPanelResizeMove, { passive: false })
  window.addEventListener('touchend', onPanelResizeEnd)
}

const onPanelResizeMove = (e) => {
  if (!panelResizing.value) return
  if (e.cancelable) e.preventDefault()
  const point = e.touches ? e.touches[0] : e
  const pct = ((window.innerWidth - point.clientX) / window.innerWidth) * 100
  width.value = Math.min(MAX_PANEL, Math.max(MIN_PANEL, pct))
}

const onPanelResizeEnd = () => {
  panelResizing.value = false
  window.removeEventListener('mousemove', onPanelResizeMove)
  window.removeEventListener('mouseup', onPanelResizeEnd)
  window.removeEventListener('touchmove', onPanelResizeMove)
  window.removeEventListener('touchend', onPanelResizeEnd)
}

// --- Прозорість / масштаб ---
const onOpacityInput = (e) => {
  opacity.value = Number(e.target.value) / 100
}

const zoomIn = () => {
  zoom.value = Math.min(MAX_ZOOM, Math.round((zoom.value + ZOOM_STEP) * 100) / 100)
}
const zoomOut = () => {
  zoom.value = Math.max(MIN_ZOOM, Math.round((zoom.value - ZOOM_STEP) * 100) / 100)
}

const toggleMode = () => {
  mode.value = isPopup.value ? 'panel' : 'popup'
}

// Twitch вимикає ввід і модерацію власнику та модераторам, якщо на фреймі
// (чи на будь-якому його предку) є CSS transform / opacity < 1 / filter —
// це та сама «Chat window is obscured by another element». Масштаб робиться
// саме через transform, а прозорість — через opacity, тож обидва регулятори
// ламають чат для модерів. Не забороняємо їх (звичайному глядачеві вони
// нічого не псують), але чесно попереджаємо, коли вони увімкнені.
// Ні чату сайту, ні Telegram це не стосується: список наш власний, і
// масштаб із прозорістю там нікому нічого не ламають
const modUnsafe = computed(
  () => isTwitchChat.value && (zoom.value !== 1 || opacity.value < 1)
)

// Масштаб фрейму Twitch робиться тільки через transform (css zoom чужому
// документу нічого не дасть), тому розмір компенсуємо у зворотний бік —
// інакше збільшений фрейм вилізе за межі колонки. Поки тягнуть межу чи
// вікно, ввід у фрейм вимикаємо: інакше він з'їдає рух мишею, і
// перетягування зривається
const frameStyle = (busy) =>
  zoom.value === 1
    ? { pointerEvents: busy ? 'none' : 'auto' }
    : {
        width: 100 / zoom.value + '%',
        height: 100 / zoom.value + '%',
        transform: `scale(${zoom.value})`,
        transformOrigin: 'top left',
        pointerEvents: busy ? 'none' : 'auto'
      }

// Попаут Twitch — чат окремим вікном, напряму на twitch.tv, без iframe.
// Це робочий обхід перевірки «chat window is obscured»: у власному вікні
// перекрити фрейм нічим, тож у стрімера й модераторів ввід і модерація
// працюють завжди, хай там що коїться на нашій сторінці.
const popoutUrl = computed(
  () => `https://www.twitch.tv/popout/${channel.value}/chat?popout=`
)

// Кнопка «відкрити окремо» у шапці: для Twitch це попаут. У власного чату
// такого «десь іще» немає — там кнопки просто немає
const popout = computed(() => {
  if (isTwitchChat.value) {
    return {
      url: popoutUrl.value,
      // Іменоване вікно: другий клік не плодить копії, а піднімає вже
      // відкрите
      target: 'twitch_chat_popout',
      features: 'width=420,height=820',
      title:
        'Відкрити чат Twitch окремим вікном (там ввід і модерація працюють завжди)'
    }
  }


  return null
})

const openPopoutWindow = () => {
  if (!popout.value) return
  window.open(popout.value.url, popout.value.target, popout.value.features)
}

const MOD_WARNING =
  'Масштаб/прозорість вимикають написання в чат для стрімера й модераторів ' +
  '(Twitch вважає фрейм перекритим). Поверніть 100% — і ввід запрацює.'

const resetModUnsafe = () => {
  zoom.value = 1
  opacity.value = 1
}

const twitchParent = computed(() =>
  import.meta.client ? window.location.hostname : 'localhost'
)

const embedSrc = computed(
  () =>
    `https://www.twitch.tv/embed/${channel.value}/chat?parent=${twitchParent.value}&darkpopout`
)

// --- Фулскрін: вікно телепортуємо всередину fullscreen-елемента, інакше
// воно лишається під ним і його просто не видно ---
const fullscreenTarget = ref('body')

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.mozFullScreenElement ||
  document.msFullscreenElement ||
  null

const handleFullscreenChange = () => {
  fullscreenTarget.value = getFullscreenElement() || 'body'
}

const FS_EVENTS = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange'
]

onMounted(() => {
  restore()
  window.addEventListener('resize', clampToViewport)
  FS_EVENTS.forEach((evt) => document.addEventListener(evt, handleFullscreenChange))
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', clampToViewport)
  FS_EVENTS.forEach((evt) => document.removeEventListener(evt, handleFullscreenChange))
  onDragEnd()
  onResizeEnd()
  onPanelResizeEnd()
})
</script>

<template>
  <!-- Панель праворуч -->
  <!--
    Ширина в % приходить інлайн-стилем, тому мобільний варіант перебиваємо
    саме !w-full: інакше клас програє інлайну і на вузькому екрані чат
    лишається смужкою в чверть ширини
  -->
  <!--
    Закритий чат не знімаємо з дерева, а схлопуємо до нуля. Через v-if
    iframe Twitch щоразу монтувався заново: чат перезавантажувався,
    підключення й історія повідомлень губились на кожне відкриття.
    Тепер він живий увесь час, міняється лише розмір.
  -->
  <div
    v-if="!isPopup"
    :class="[
      'relative flex h-full overflow-hidden',
      'transition-[width,height] duration-300 ease-out',
      forceRow
        ? 'flex-shrink-0'
        : 'md:flex-shrink-0 max-md:!w-full max-md:h-auto',
      // На вузькому екрані чат стоїть під плеєром і займає висоту, а не
      // ширину — там схлопувати треба саме її
      open
        ? (forceRow ? '' : 'max-md:flex-1')
        : 'max-md:!h-0 max-md:!flex-none min-w-0'
    ]"
    :style="{ width: open ? width + '%' : '0%' }"
  >
    <!-- Ручка ширини: окремою смугою зліва, iframe не перекриває -->
    <div
      class="max-md:hidden flex-shrink-0 bg-base-300 hover:bg-primary w-[5px] h-full transition-colors cursor-ew-resize"
      :class="{ 'bg-primary': panelResizing }"
      @mousedown.prevent="onPanelResizeStart"
      @touchstart.prevent="onPanelResizeStart"
    />

    <div class="flex flex-col flex-1 min-w-0 h-full">
      <div class="flex flex-shrink-0 items-center gap-2 bg-base-200 px-2 py-1.5">
        <button
          class="btn btn-ghost btn-xs btn-circle shrink-0"
          title="Зменшити масштаб"
          @click="zoomOut"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12"
            ></line>
          </svg>
        </button>
        <span class="opacity-70 w-8 text-[10px] text-center shrink-0">
          {{ Math.round(zoom * 100) }}%
        </span>
        <button
          class="btn btn-ghost btn-xs btn-circle shrink-0"
          title="Збільшити масштаб"
          @click="zoomIn"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line
              x1="12"
              y1="5"
              x2="12"
              y2="19"
            ></line>
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12"
            ></line>
          </svg>
        </button>

        <button
          v-if="modUnsafe"
          class="text-warning btn btn-ghost btn-xs btn-circle shrink-0"
          :title="MOD_WARNING"
          @click="resetModUnsafe"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line
              x1="12"
              y1="9"
              x2="12"
              y2="13"
            />
            <line
              x1="12"
              y1="17"
              x2="12.01"
              y2="17"
            />
          </svg>
        </button>

        <div class="flex-1"></div>

        <button
          class="btn btn-ghost btn-xs btn-circle shrink-0"
          title="У плаваюче вікно"
          @click="toggleMode"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect
              x="3"
              y="4"
              width="18"
              height="14"
              rx="2"
            />
            <rect
              x="11"
              y="11"
              width="8"
              height="6"
              rx="1"
              fill="currentColor"
            />
          </svg>
        </button>

        <button
          class="btn btn-ghost btn-xs btn-circle shrink-0"
          v-if="popout"
          :title="popout.title"
          @click="openPopoutWindow"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
          >
            <path
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M21 9.75V3h-6.75M21 3l-8.25 8.25M9.4 3c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C3 6.04 3 7.16 3 9.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C6.04 21 7.16 21 9.4 21h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C21 17.96 21 16.84 21 14.6v-1.1"
            />
          </svg>
        </button>

        <button
          class="btn btn-ghost btn-xs btn-circle shrink-0"
          title="Закрити чат"
          @click="open = false"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line
              x1="18"
              y1="6"
              x2="6"
              y2="18"
            ></line>
            <line
              x1="6"
              y1="6"
              x2="18"
              y2="18"
            ></line>
          </svg>
        </button>
      </div>

      <div class="relative flex-1 overflow-hidden">
        <ClientOnly>
          <!--
            Власний чат на вебсокетах: тут і список, і написання наші, тож
            і масштаб чесний — css zoom на всю розкладку разом
          -->
          <div
            v-if="isLiveChat"
            class="flex flex-col w-full h-full"
            :style="zoom === 1 ? null : { zoom }"
          >
            <ChatLiveBody
              :room="chatPageId"
              @open-chat="open = true"
            />
          </div>

          <iframe
            v-else
            :src="embedSrc"
            :style="frameStyle(panelResizing)"
            class="bg-base-300 w-full h-full"
            frameborder="0"
          ></iframe>
        </ClientOnly>
      </div>
    </div>
  </div>

  <!-- Плаваюче вікно -->
  <ClientOnly>
    <Teleport
      v-if="open && isPopup"
      :to="fullscreenTarget"
    >
      <div
        class="z-[2147483000] fixed flex flex-col shadow-2xl border border-base-content/10 rounded-lg overflow-hidden select-none"
        :style="{
          left: pos.x + 'px',
          top: pos.y + 'px',
          width: size.width + 'px',
          height: size.height + 'px',
          opacity: opacity
        }"
      >
        <div
          class="flex flex-shrink-0 items-center gap-2 bg-base-200 px-2 py-1.5 cursor-move"
          @mousedown="onDragStart"
          @touchstart="onDragStart"
        >
          <button
            class="btn btn-ghost btn-xs btn-circle shrink-0"
            title="Зменшити масштаб"
            @click="zoomOut"
            @mousedown.stop
            @touchstart.stop
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
              ></line>
            </svg>
          </button>
          <span class="opacity-70 w-8 text-[10px] text-center shrink-0">
            {{ Math.round(zoom * 100) }}%
          </span>
          <button
            class="btn btn-ghost btn-xs btn-circle shrink-0"
            title="Збільшити масштаб"
            @click="zoomIn"
            @mousedown.stop
            @touchstart.stop
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line
                x1="12"
                y1="5"
                x2="12"
                y2="19"
              ></line>
              <line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
              ></line>
            </svg>
          </button>

          <button
            v-if="modUnsafe"
            class="text-warning btn btn-ghost btn-xs btn-circle shrink-0"
            :title="MOD_WARNING"
            @click="resetModUnsafe"
            @mousedown.stop
            @touchstart.stop
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line
                x1="12"
                y1="9"
                x2="12"
                y2="13"
              />
              <line
                x1="12"
                y1="17"
                x2="12.01"
                y2="17"
              />
            </svg>
          </button>

          <div class="flex-1"></div>

          <button
            class="btn btn-ghost btn-xs btn-circle shrink-0"
            v-if="popout"
            :title="popout.title"
            @click="openPopoutWindow"
            @mousedown.stop
            @touchstart.stop
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
            >
              <path
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M21 9.75V3h-6.75M21 3l-8.25 8.25M9.4 3c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C3 6.04 3 7.16 3 9.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C6.04 21 7.16 21 9.4 21h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C21 17.96 21 16.84 21 14.6v-1.1"
              />
            </svg>
          </button>

          <button
            class="btn btn-ghost btn-xs btn-circle shrink-0"
            title="Повернути в панель"
            @click="toggleMode"
            @mousedown.stop
            @touchstart.stop
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="16"
                rx="2"
              />
              <line
                x1="15"
                y1="4"
                x2="15"
                y2="20"
              />
            </svg>
          </button>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="opacity-70 shrink-0"
          >
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle
              cx="12"
              cy="12"
              r="3"
            />
          </svg>
          <input
            type="range"
            min="20"
            max="100"
            step="5"
            :value="opacity * 100"
            class="w-16 range range-primary range-xs shrink-0"
            title="Прозорість чату"
            @input="onOpacityInput"
            @mousedown.stop
            @touchstart.stop
          />

          <button
            class="btn btn-ghost btn-xs btn-circle shrink-0"
            title="Закрити чат"
            @click="open = false"
            @mousedown.stop
            @touchstart.stop
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              ></line>
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              ></line>
            </svg>
          </button>
        </div>

        <div class="relative flex-1 overflow-hidden">
          <!-- Власний чат — той самий компонент, що й у панелі -->
          <div
            v-if="isLiveChat"
            class="flex flex-col w-full h-full"
            :style="zoom === 1 ? null : { zoom }"
          >
            <ChatLiveBody
              :room="chatPageId"
              @open-chat="open = true"
            />
          </div>

          <iframe
            v-else
            :src="embedSrc"
            :style="frameStyle(dragging || resizing)"
            class="bg-base-300 w-full h-full"
            frameborder="0"
          ></iframe>
        </div>

        <!--
          Ручка ресайзу мусить бути саме окремим рядком flex-колонки, а не
          absolute bottom-0: у другому випадку вона накриває нижні 12px
          фрейму, і Twitch вимикає ввід власнику та модераторам. Це не
          теорія — IntersectionObserver{trackVisibility} на фреймі віддає
          isVisible:false при intersectionRatio:1, і саме так Twitch це й
          перевіряє.
        -->
        <div
          class="flex flex-shrink-0 justify-end items-center bg-base-200 px-1 w-full h-3 cursor-nwse-resize"
          @mousedown="onResizeStart"
          @touchstart="onResizeStart"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            class="opacity-40"
          >
            <path
              fill="currentColor"
              d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z"
            />
          </svg>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>
